import axios from 'axios';
import { isPublicPath } from './publicPaths';
import { sharedRefresh } from './sessionRefresh';

const API = process.env.REACT_APP_BACKEND_URL;

// Flag to prevent multiple refresh requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Function to refresh the access token.
//
// 2026-08-16 rebuild — delegates to `sessionRefresh.sharedRefresh()`
// so axios interceptors and `_kmRefreshWrapped` share ONE in-flight
// refresh.  Two concurrent 401s (say an axios call + a raw pdf.js
// fetch) therefore issue ONE POST /api/auth/refresh, not two.
const refreshAccessToken = async () => {
  return sharedRefresh();
};

// Function to check if token is expired or about to expire (within 1 minute)
const isTokenExpired = () => {
  const expiresAt = localStorage.getItem('tokenExpiresAt');
  if (!expiresAt) return true;
  
  // Consider token expired if less than 1 minute remaining
  return Date.now() > (parseInt(expiresAt) - 60000);
};

// Function to clear auth data and redirect to login
const AUTH_STORAGE_KEYS = [
  'accessToken', 'refreshToken', 'tokenExpiresAt',
  'userId', 'userEmail', 'userName',
  'companyId', 'companyName', 'companySlug',
];

const handleAuthFailure = () => {
  AUTH_STORAGE_KEYS.forEach((k) => localStorage.removeItem(k));

  // Only redirect if we're on a route that REQUIRES auth.
  // Public routes (login, register, forgot-password, reset-password, portal
  // tokens, legal pages, etc.) must never get force-bounced when a background
  // API call 401s — otherwise the user sees a flash of the page they wanted
  // and gets yanked to /login.
  if (typeof window !== 'undefined' && !isPublicPath(window.location.pathname)) {
    window.location.href = '/login';
  }
};

// Explicit user-initiated logout (FixedBottomNav "Log out"). Best-effort
// server-side refresh-token invalidation, then ALWAYS clears the session
// and lands on /login (unlike handleAuthFailure, which skips the redirect
// on public paths).
export const logoutAndRedirect = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      await axios.post(`${API}/api/auth/logout`, { refresh_token: refreshToken });
    }
  } catch (e) {
    // Server-side invalidation is best-effort — local logout proceeds.
  }
  AUTH_STORAGE_KEYS.forEach((k) => localStorage.removeItem(k));
  window.location.href = '/login';
};

// Create axios instance with default configuration
const axiosInstance = axios.create();

// Request interceptor - add token and check expiration
axiosInstance.interceptors.request.use(
  async (config) => {
    // Skip auth for login/register/refresh endpoints
    const authEndpoints = ['/api/auth/login', '/api/auth/register', '/api/auth/refresh'];
    if (authEndpoints.some(endpoint => config.url?.includes(endpoint))) {
      return config;
    }
    
    // Check if token is about to expire and refresh proactively
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (accessToken && refreshToken && isTokenExpired()) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          isRefreshing = false;
          processQueue(null, newToken);
          config.headers['Authorization'] = `Bearer ${newToken}`;
        } catch (error) {
          isRefreshing = false;
          processQueue(error, null);
          // Don't redirect here, let the response interceptor handle it
        }
      } else {
        // Queue this request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              config.headers['Authorization'] = `Bearer ${token}`;
              resolve(config);
            },
            reject: (err) => reject(err)
          });
        });
      }
    } else if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    
    // Also include company_id header as backup (for backward compatibility)
    const companyId = localStorage.getItem('companyId');
    if (companyId) {
      config.headers['X-Company-Id'] = companyId;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 errors
//
// Phase 6.6 (L12): the 401-handler historically did "refresh OR force-logout
// redirect" on every 401, regardless of whether the 401 came from a JWT
// expiry or a feature-level lock (e.g. vault session expired). The vault
// 401 ("Vault is locked") was wiping the user's JWT and bouncing them to
// /login even though their session was perfectly valid.
//
// Per-request opt-out: set `config._skipAuthRedirect = true` on the call
// to tell the interceptor:
//   - genuine JWT expiry → still attempt refresh as usual
//   - feature-level 401 (refresh succeeded but call still 401s, OR no
//     refresh token at all) → DO NOT bounce to /login. Let the caller
//     handle the 401 in its own catch block.
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const skipAuthRedirect = originalRequest?._skipAuthRedirect === true;
    
    // If 401 and not already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        if (!skipAuthRedirect) handleAuthFailure();
        return Promise.reject(error);
      }
      
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          isRefreshing = false;
          processQueue(null, newToken);
          
          // Retry the original request with new token
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          processQueue(refreshError, null);
          if (!skipAuthRedirect) handleAuthFailure();
          return Promise.reject(refreshError);
        }
      } else {
        // Queue this request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              resolve(axiosInstance(originalRequest));
            },
            reject: (err) => reject(err)
          });
        });
      }
    }
    
    return Promise.reject(error);
  }
);

// Configure default axios instance as well (for any direct axios calls)
axios.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    
    const companyId = localStorage.getItem('companyId');
    if (companyId) {
      config.headers['X-Company-Id'] = companyId;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Also add response interceptor to default axios
// Same per-request `_skipAuthRedirect` honoured here (Phase 6.6 / L12).
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const skipAuthRedirect = error.config?._skipAuthRedirect === true;
    if (error.response?.status === 401) {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken && !error.config._retry) {
        error.config._retry = true;
        try {
          const newToken = await refreshAccessToken();
          error.config.headers['Authorization'] = `Bearer ${newToken}`;
          return axios(error.config);
        } catch (refreshError) {
          if (!skipAuthRedirect) handleAuthFailure();
          return Promise.reject(refreshError);
        }
      } else {
        if (!skipAuthRedirect) handleAuthFailure();
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
